import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { MagnifyingGlass } from "@phosphor-icons/react";
import StatusBadge from "@/components/StatusBadge";

export default function GlobalSearch({ onOpen }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  // Track a monotonically-increasing "generation" per keystroke so a stale
  // response (e.g. slow "124" finishing AFTER the newer "1247") does NOT
  // overwrite the fresh results. Cancel in-flight XHRs on top of that.
  // Bug fixed 2026-09-22: user searched "1247" but saw 1124/1240/1241… —
  // the older "124" response was landing after the "1247" one.
  const genRef = useRef(0);
  const abortRef = useRef(null);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      // Cancel any earlier in-flight search
      if (abortRef.current) { try { abortRef.current.abort(); } catch (_e) {} }
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      const myGen = ++genRef.current;
      try {
        const { data } = await api.get(
          `/orders/search?q=${encodeURIComponent(q.trim())}`,
          { signal: ctrl.signal },
        );
        if (myGen !== genRef.current) return;   // stale — ignore
        setResults(data);
        setOpen(true);
      } catch (e) {
        if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;
        // network / server errors — silently keep old results out of the way
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="relative w-full max-w-md" data-testid="global-search-container">
      <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => q && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        placeholder="Search Order # or Mobile…"
        data-testid="global-search-input"
        className="pl-9 bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm font-mono-num"
      />
      {open && results.length > 0 && (
        <div className="absolute z-40 top-11 left-0 right-0 surface rounded-sm shadow-2xl max-h-80 overflow-auto">
          {results.map((r) => (
            <button
              key={r.id}
              onMouseDown={(e) => {
                e.preventDefault();
                onOpen(r.id);
                setOpen(false);
              }}
              className="w-full text-left px-3 py-2 hover:bg-slate-800 border-b border-slate-800 last:border-0"
              data-testid={`search-result-${r.order_number}`}
            >
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <div className="font-mono-num text-orange-400 text-sm">#{r.order_number}</div>
                  <div className="text-slate-200 text-sm truncate">{r.customer_name} · <span className="font-mono-num text-slate-400">{r.mobile}</span></div>
                  {r.address && (
                    <div className="text-[11px] text-slate-500 truncate">
                      {r.address}
                    </div>
                  )}
                </div>
                <StatusBadge status={r.status} />
              </div>
            </button>
          ))}
        </div>
      )}
      {open && q && results.length === 0 && (
        <div className="absolute z-40 top-11 left-0 right-0 surface rounded-sm px-3 py-3 text-slate-500 text-sm">
          No matches.
        </div>
      )}
    </div>
  );
}
